import type {
  FleetCommand,
  FleetDevice,
  NetworkAddress,
  NetworkInterfaceStatus,
  ServerToUiMessage,
} from '@manikin-overmind/protocol';
import { computed, signal } from '@preact/signals';
import { authenticatedFetch, redirectToSignIn, sessionIsAuthenticated } from './auth';

export type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'offline';

export const devices = signal<FleetDevice[]>([]);
export const commands = signal<FleetCommand[]>([]);
export const connectionState = signal<ConnectionState>('connecting');
export const loading = signal(true);
export const loadError = signal<string | null>(null);
export const lastLiveUpdate = signal<Date | null>(null);

type UiMessageListener = (message: unknown) => void;
const uiMessageListeners = new Set<UiMessageListener>();
let activeFleetSocket: WebSocket | null = null;
const MAX_IN_MEMORY_COMMANDS = 500;

export function subscribeToUiMessages(listener: UiMessageListener): () => void {
  uiMessageListeners.add(listener);
  return () => uiMessageListeners.delete(listener);
}

export function sendUiMessage(message: unknown): boolean {
  if (activeFleetSocket?.readyState !== WebSocket.OPEN) return false;
  activeFleetSocket.send(JSON.stringify(message));
  return true;
}

export const sortedDevices = computed(() =>
  [...devices.value].sort((left, right) => {
    if (left.presence !== right.presence) return left.presence === 'online' ? -1 : 1;
    const leftName = deviceLabel(left);
    const rightName = deviceLabel(right);
    return leftName.localeCompare(rightName);
  }),
);

export const onlineCount = computed(
  () => devices.value.filter((device) => device.presence === 'online').length,
);

function isUsefulNetwork(network: NetworkInterfaceStatus): boolean {
  return network.kind === 'wifi' || network.kind === 'ethernet';
}

function isLoopbackAddress(address: NetworkAddress): boolean {
  return address.address === '::1' || address.address.startsWith('127.');
}

export function usefulAddresses(network: NetworkInterfaceStatus): NetworkAddress[] {
  const addresses = network.addresses.filter((address) => !isLoopbackAddress(address));
  const nonLinkLocal = addresses.filter((address) => address.scope !== 'link');
  return nonLinkLocal.length > 0 ? nonLinkLocal : addresses;
}

function networkScore(network: NetworkInterfaceStatus): number {
  let score = 0;
  if (network.defaultRoute) score += 8;
  if (usefulAddresses(network).length > 0) score += 4;
  if (network.up) score += 2;
  if (network.carrier !== false) score += 1;
  return score;
}

export function usefulNetworkInterfaces(
  interfaces: NetworkInterfaceStatus[] | undefined,
): NetworkInterfaceStatus[] {
  return (interfaces ?? [])
    .filter(isUsefulNetwork)
    .sort((left, right) => networkScore(right) - networkScore(left));
}

export function primaryNetworkInterface(
  interfaces: NetworkInterfaceStatus[] | undefined,
): NetworkInterfaceStatus | undefined {
  return usefulNetworkInterfaces(interfaces)[0];
}

export function primaryNetworkAddress(network: NetworkInterfaceStatus | undefined): string | undefined {
  if (!network) return undefined;
  const addresses = usefulAddresses(network);
  return (
    addresses.find((address) => address.family === 'ipv4') ??
    addresses.find((address) => address.family === 'ipv6')
  )?.address;
}

export function applyUiMessage(message: ServerToUiMessage): void {
  if (message.type === 'fleet_snapshot') {
    devices.value = message.devices;
    commands.value = message.commands;
  } else if (message.type === 'device_changed') {
    devices.value = upsert(devices.value, message.device);
  } else if (message.type === 'command_changed') {
    commands.value = upsert(commands.value, message.command).slice(0, MAX_IN_MEMORY_COMMANDS);
  }
  lastLiveUpdate.value = new Date();
}

export function upsertCommand(command: FleetCommand): void {
  commands.value = upsert(commands.value, command).slice(0, MAX_IN_MEMORY_COMMANDS);
}

export function deviceLabel(device: FleetDevice): string {
  return device.displayName ?? device.status?.hostname ?? device.identityMac;
}

const PRODUCT_NAME_BY_ID: Readonly<Record<string, string>> = {
  '204-30150': 'SimPad Plus',
  '204-30250': 'Link Box Plus',
  '20-11480': 'CPU Module',
  '20-19601': 'SimPad Plus 2',
  '20-19602': 'Link Box Plus 2',
  '20-19560': 'CPU Module 2',
};

const MANIKIN_NAME_BY_ID: Readonly<Record<string, string>> = {
  '150': 'Nursing Anne Simulator',
  '32': 'AnnieOne',
  '160': 'MamaAnne',
  '100': 'SimBaby',
  '101': 'SimNewB',
};

export function formatProductId(value: string | undefined): string {
  if (!value) return '';
  const name = PRODUCT_NAME_BY_ID[value.split('/')[0] ?? ''];
  return name ? `${name} (${value})` : value;
}

export function formatManikinId(value: string | undefined): string {
  if (!value) return '';
  const name = manikinName(value);
  return name ? `${name} (${value})` : value;
}

export function manikinName(value: string | undefined): string | undefined {
  return value ? MANIKIN_NAME_BY_ID[value] : undefined;
}

const MANIKIN_TONE_BY_ID: Readonly<Record<string, string>> = {
  '150': 'nas',
  '32': 'annie-one',
  '160': 'mama-anne',
  '100': 'sim-baby',
  '101': 'sim-new-b',
};

export function manikinChipTone(value: string | undefined): string {
  return (value && MANIKIN_TONE_BY_ID[value]) || 'unknown';
}

export function distinctManikinSerial(
  manikinSerial: string | undefined,
  productSerial: string | undefined,
): string {
  const value = manikinSerial?.trim();
  if (!value || value === productSerial?.trim()) return '';
  return value;
}

function upsert<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((candidate) => candidate.id === item.id);
  if (index < 0) return [item, ...items];
  const next = [...items];
  next[index] = item;
  return next;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await authenticatedFetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.json() as Promise<T>;
}

export async function loadFleet(): Promise<void> {
  try {
    const [deviceResult, commandResult] = await Promise.all([
      fetchJson<{ devices: FleetDevice[] }>('/api/devices'),
      fetchJson<{ commands: FleetCommand[] }>('/api/commands?limit=50'),
    ]);
    devices.value = deviceResult.devices;
    commands.value = commandResult.commands;
    loadError.value = null;
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Could not load the fleet';
  } finally {
    loading.value = false;
  }
}

export async function createRebootCommand(deviceId: string): Promise<FleetCommand> {
  const response = await authenticatedFetch(`/api/devices/${encodeURIComponent(deviceId)}/commands`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ action: 'reboot' }),
  });
  const body = (await response.json().catch(() => null)) as { command?: FleetCommand; error?: string } | null;
  if (!response.ok || !body?.command) {
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  upsertCommand(body.command);
  return body.command;
}

export async function updateDeviceDisplayName(
  deviceId: string,
  displayName: string | null,
): Promise<FleetDevice> {
  const response = await authenticatedFetch(`/api/devices/${encodeURIComponent(deviceId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  const body = (await response.json().catch(() => null)) as {
    device?: FleetDevice;
    error?: string;
  } | null;
  if (!response.ok || !body?.device) {
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  devices.value = upsert(devices.value, body.device);
  return body.device;
}

export function connectFleetSocket(onDispose: (dispose: () => void) => void): void {
  let socket: WebSocket | null = null;
  let reconnectTimer: number | undefined;
  let stopped = false;
  let attempts = 0;

  const connect = () => {
    if (stopped || socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) {
      return;
    }
    connectionState.value = attempts === 0 ? 'connecting' : 'reconnecting';
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const current = new WebSocket(`${scheme}//${window.location.host}/ws/ui`);
    socket = current;

    current.addEventListener('open', () => {
      if (stopped || socket !== current) {
        current.close();
        return;
      }
      activeFleetSocket = current;
      attempts = 0;
      connectionState.value = 'live';
    });
    current.addEventListener('message', (event) => {
      if (socket !== current) return;
      try {
        const parsed = JSON.parse(String(event.data)) as ServerToUiMessage;
        for (const listener of uiMessageListeners) listener(parsed);
        if (
          parsed.type === 'fleet_snapshot' ||
          parsed.type === 'device_changed' ||
          parsed.type === 'command_changed'
        ) {
          applyUiMessage(parsed);
        }
      } catch {
        // Ignore malformed frames; a later full snapshot repairs local state.
      }
    });
    const handleClose = async (closed: WebSocket, event: CloseEvent) => {
      if (socket !== closed) return;
      socket = null;
      if (activeFleetSocket === closed) activeFleetSocket = null;
      if (stopped) return;
      const sessionExpired = event.code === 1006 && (await sessionIsAuthenticated()) === false;
      if (socket !== null || stopped) return;
      if (event.code === 4003 || sessionExpired) {
        stopped = true;
        connectionState.value = 'offline';
        redirectToSignIn();
        return;
      }
      attempts += 1;
      connectionState.value = navigator.onLine ? 'reconnecting' : 'offline';
      const delay = Math.min(1_000 * 2 ** Math.min(attempts - 1, 4), 15_000);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(connect, delay + Math.random() * 350);
    };
    current.addEventListener('close', (event) => {
      void handleClose(current, event);
    });
    current.addEventListener('error', () => current.close());
  };

  const reconnectNow = () => {
    if (stopped || socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) {
      return;
    }
    if (reconnectTimer) window.clearTimeout(reconnectTimer);
    attempts = 0;
    connect();
  };

  window.addEventListener('online', reconnectNow);
  connect();

  onDispose(() => {
    stopped = true;
    if (reconnectTimer) window.clearTimeout(reconnectTimer);
    window.removeEventListener('online', reconnectNow);
    socket?.close();
    activeFleetSocket = null;
  });
}
